import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { pageHead, robotsMetaContent } from '@/lib/robotsMeta';

function headElement<T extends HTMLElement>(selector: string, create: () => T): T {
  let el = document.head.querySelector<T>(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
}

// Rewrites the robots meta, title, description and canonical on first mount
// and on every route change, so a client-side navigation off a marketing
// page drops back to noindex and the default title, and loses the
// canonical the server sent for the marketing page. The policy itself
// lives in lib/robotsMeta.ts.
export function RobotsMeta() {
  const { pathname } = useLocation();
  useEffect(() => {
    const robots = headElement('meta[name="robots"]', () => {
      const m = document.createElement('meta');
      m.name = 'robots';
      return m;
    });
    robots.content = robotsMetaContent(window.location.hostname, pathname);

    const head = pageHead(window.location.origin, pathname);
    document.title = head.title;
    const description = headElement('meta[name="description"]', () => {
      const m = document.createElement('meta');
      m.name = 'description';
      return m;
    });
    description.content = head.description;

    if (head.canonical) {
      const link = headElement('link[rel="canonical"]', () => {
        const l = document.createElement('link');
        l.rel = 'canonical';
        return l;
      });
      link.href = head.canonical;
    } else {
      document.head.querySelector('link[rel="canonical"]')?.remove();
    }
  }, [pathname]);
  return null;
}
